import { FormReviewData } from "@/types";
import { useEffect, useState } from "react";
import {
  medicationService,
  type AIRecommendationsResponse,
  type PharmacistAddedItem,
} from "@/services/medications";
import {
  pharmacyService,
  type VaccineProtocol,
  type MedicationProtocol,
} from "@/services/pharmacy";
import { usePharmacyContext } from "@/contexts/PharmacyContext";

// Combined type for manual selections with both protocol and pharmacist item data
interface ManualVaccineSelection {
  protocol: VaccineProtocol;
  pharmacistItem: PharmacistAddedItem;
}

interface ManualMedicationSelection {
  protocol: MedicationProtocol;
  pharmacistItem: PharmacistAddedItem;
}

interface PrintableConsultationProps {
  formData: FormReviewData;
  selectionVersion?: number;
  aiRecommendations?: AIRecommendationsResponse | null;
}

export const PrintableConsultation: React.FC<PrintableConsultationProps> = ({
  formData,
  selectionVersion,
  aiRecommendations: externalAiRecommendations,
}: PrintableConsultationProps) => {
  const { currentPharmacy } = usePharmacyContext();
  const [aiRecommendations, setAiRecommendations] =
    useState<AIRecommendationsResponse | null>(
      externalAiRecommendations || null,
    );
  const [manualSelections, setManualSelections] = useState<{
    vaccines: ManualVaccineSelection[];
    medications: ManualMedicationSelection[];
  }>({ vaccines: [], medications: [] });
  // Protocol lookup maps for displaying selected items with protocol data
  const [vaccineProtocolMap, setVaccineProtocolMap] = useState<
    Record<string, VaccineProtocol>
  >({});
  const [medicationProtocolMap, setMedicationProtocolMap] = useState<
    Record<string, MedicationProtocol>
  >({});

  // Sync with external recommendations when they change
  useEffect(() => {
    if (externalAiRecommendations) {
      setAiRecommendations(externalAiRecommendations);
    }
  }, [externalAiRecommendations]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!formData.travelerId) return;

      try {
        // Use external recommendations when available to avoid duplicate API calls
        // The parent component already has fresh data
        let recommendations: AIRecommendationsResponse;
        if (externalAiRecommendations) {
          // Use external recommendations - parent already has latest data
          recommendations = externalAiRecommendations;
        } else {
          // Fallback: fetch only if nothing provided
          recommendations = await medicationService.getAIRecommendations(
            formData.travelerId,
            true,
          );
        }
        setAiRecommendations(recommendations);

        // Fetch protocols for lookup
        const [vaccineData, medicationData] = await Promise.all([
          pharmacyService.getProtocols({ type: "vaccine" }),
          pharmacyService.getProtocols({ type: "medication" }),
        ]);

        // Build vaccine protocol lookup map
        const vaccineMap: Record<string, VaccineProtocol> = {};
        vaccineData.protocols.vaccines.forEach((v) => {
          vaccineMap[v.protocolId] = v;
        });
        setVaccineProtocolMap(vaccineMap);

        // Build medication protocol lookup map
        const medMap: Record<string, MedicationProtocol> = {};
        medicationData.protocols.medications.forEach((m) => {
          medMap[m.protocolId] = m;
        });
        setMedicationProtocolMap(medMap);

        // Fetch manual selections from recommendation API
        const manualVaccines: ManualVaccineSelection[] = [];
        const manualMedications: ManualMedicationSelection[] = [];

        // Process pharmacist-added vaccines
        const pharmacistAddedVaccines =
          recommendations.recommendation.pharmacistAddedVaccines || [];
        for (const addedVaccine of pharmacistAddedVaccines) {
          // Include if isSelected is true or not explicitly false (handles undefined/null)
          if (addedVaccine.isSelected !== false) {
            // Try to find the protocol in the fetched protocols
            let protocol = vaccineMap[addedVaccine.protocol_id];

            // If not found, construct from protocolMetadata or create a basic one
            if (!protocol) {
              protocol = {
                protocolId: addedVaccine.protocol_id,
                displayName: addedVaccine.name,
                therapyClass: addedVaccine.therapy_class,
                protocolType: "vaccine",
                ageGroup: addedVaccine.age_group || "adults",
                category: "travel",
                ...(addedVaccine.protocolMetadata || {}),
              } as VaccineProtocol;
            }

            manualVaccines.push({
              protocol,
              pharmacistItem: addedVaccine,
            });
          }
        }

        // Process pharmacist-added medications
        const pharmacistAddedMedications =
          recommendations.recommendation.pharmacistAddedMedications || [];
        for (const addedMedication of pharmacistAddedMedications) {
          // Include if isSelected is true or not explicitly false (handles undefined/null)
          if (addedMedication.isSelected !== false) {
            // Try to find the protocol in the fetched protocols
            let protocol = medMap[addedMedication.protocol_id];

            // If not found, construct from protocolMetadata or create a basic one
            if (!protocol) {
              protocol = {
                protocolId: addedMedication.protocol_id,
                displayName: addedMedication.name,
                therapyClass: addedMedication.therapy_class,
                protocolType: "medication",
                ageGroup: addedMedication.age_group || "adults",
                ...(addedMedication.protocolMetadata || {}),
              } as MedicationProtocol;
            }

            manualMedications.push({
              protocol,
              pharmacistItem: addedMedication,
            });
          }
        }

        setManualSelections({
          vaccines: manualVaccines,
          medications: manualMedications,
        });
      } catch (error) {
        // Silent fail
      }
    };

    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.travelerId, selectionVersion, externalAiRecommendations]);

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    if (!dateString) return "Not specified";

    try {
      const datePart = dateString.split("T")[0];
      const parts = datePart.split("-");

      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        const localDate = new Date(year, month, day);

        return localDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  // Helper function to calculate trip duration
  const calculateTripDuration = (departureDate: string, returnDate: string) => {
    if (!departureDate || !returnDate) return "Unknown duration";

    try {
      const depParts = departureDate.split("T")[0].split("-");
      const retParts = returnDate.split("T")[0].split("-");

      const departure = new Date(
        parseInt(depParts[0]),
        parseInt(depParts[1]) - 1,
        parseInt(depParts[2]),
      );
      const returnD = new Date(
        parseInt(retParts[0]),
        parseInt(retParts[1]) - 1,
        parseInt(retParts[2]),
      );

      const diffTime = Math.abs(returnD.getTime() - departure.getTime());
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Same day";
      return `${diffDays} day${diffDays === 1 ? "" : "s"}`;
    } catch {
      return "Unknown duration";
    }
  };

  // Helper to format dosing info - handles adult and pediatric cases
  const formatDosing = (protocol: any): string => {
    if (!protocol?.dosing) return "N/A";

    const adultDose = protocol.dosing.adult?.dose;
    const pediatricDosing = protocol.dosing.pediatric;
    const hasPediatric =
      Array.isArray(pediatricDosing) && pediatricDosing.length > 0;

    const parts: string[] = [];

    // Add adult dose if available
    if (adultDose) {
      parts.push(`Adult: ${adultDose}`);
    }

    // Add all pediatric doses with age ranges
    if (hasPediatric) {
      pediatricDosing.forEach((ped: any) => {
        if (ped.age_range && ped.dose) {
          parts.push(`${ped.age_range}: ${ped.dose}`);
        }
      });
    }

    if (parts.length === 0) return "N/A";
    return parts.join("; ");
  };

  // Helper to format route info
  const formatRoute = (protocol: any): string => {
    if (!protocol) return "injection";

    const adultRoute = protocol.dosing?.adult?.route;
    const protocolRoute = protocol.route;

    if (adultRoute) return adultRoute;
    if (protocolRoute) return protocolRoute;

    // Default to injection for vaccines
    return "injection";
  };

  // Get notes from pharmacyActions
  const notes =
    formData.pharmacyActions?.filter(
      (action) => action.type === "form_note_added",
    ) || [];

  // Format timestamp for notes
  const formatDateTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        padding: "10px 20px",
        maxWidth: "11in",
        margin: "0 auto",
        backgroundColor: "white",
        color: "#000",
        fontSize: "10px",
      }}
    >
      {/* Print styles */}
      <style>
        {`
          @media print {
            @page {
              margin: 8mm 8mm 12mm 8mm;
            }
          }
        `}
      </style>

      {/* Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "12px",
          paddingBottom: "10px",
          borderBottom: "2px solid #000",
        }}
      >
        <h1
          style={{
            margin: "0 0 4px 0",
            fontSize: "18px",
            fontWeight: "bold",
            color: "#000",
            letterSpacing: "1px",
          }}
        >
          {currentPharmacy?.name?.toUpperCase() || "VOYAX HEALTH"}
        </h1>
        {currentPharmacy?.location && (
          <div style={{ fontSize: "10px", color: "#333", marginBottom: "4px" }}>
            {currentPharmacy.location}
          </div>
        )}
        <div
          style={{
            fontSize: "11px",
            fontWeight: "normal",
            color: "#333",
            marginTop: "4px",
          }}
        >
          Travel Health Consultation
        </div>
      </div>

      {/* Consultation Meta */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "12px",
          padding: "8px 0",
          fontSize: "10px",
          borderBottom: "1px solid #ccc",
        }}
      >
        <div>
          <div style={{ marginBottom: "4px" }}>
            <strong>Consultation #:</strong> {formData.consultationNumber}
          </div>
          <div>
            <strong>Traveler:</strong> {formData.travelerName}
          </div>
        </div>
        <div>
          <div style={{ marginBottom: "4px" }}>
            <strong>Status:</strong> {formData.formStatus}
          </div>
          <div>
            <strong>Printed:</strong> {new Date().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Pharmacy Selections Section */}
      {aiRecommendations && (
        <div
          style={{
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#000",
              margin: "0 0 12px 0",
              padding: "6px 0",
              borderBottom: "2px solid #000",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Rx
          </h3>

          {/* Selected Vaccines Table */}
          {(() => {
            // Get all selected vaccines from AI recommendations
            const selectedAIVaccines =
              aiRecommendations.recommendation.vaccines.filter(
                (v) => v.isSelected === true,
              );
            // Get manually added vaccines (from pharmacistAddedVaccines)
            const selectedManualVaccines = manualSelections.vaccines;
            // Get not recommended vaccines that are selected
            const selectedNotRecVaccines = (
              aiRecommendations.recommendation.notRecommended?.vaccines || []
            ).filter((v: any) => v.isSelected === true);

            const hasVaccines =
              selectedAIVaccines.length > 0 ||
              selectedManualVaccines.length > 0 ||
              selectedNotRecVaccines.length > 0;

            if (!hasVaccines) return null;

            return (
              <div style={{ marginBottom: "12px" }}>
                <h4
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#000",
                    margin: "0 0 6px 0",
                  }}
                >
                  Selected Vaccines
                </h4>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                          width: "30px",
                        }}
                      >
                        ✓
                      </th>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Vaccine Name
                      </th>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Source
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* AI Recommended Vaccines */}
                    {selectedAIVaccines.map((vaccine, idx) => {
                      const protocol = vaccineProtocolMap[vaccine.protocol_id];
                      return (
                        <tr key={`ai-${idx}`}>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            V
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            {protocol?.displayName || vaccine.vaccine_name}
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              fontStyle: "italic",
                            }}
                          >
                            AI Recommended
                          </td>
                        </tr>
                      );
                    })}

                    {/* Manually Added Vaccines */}
                    {selectedManualVaccines.map((item, idx) => (
                      <tr key={`manual-${idx}`}>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          V
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                          }}
                        >
                          {item.protocol.displayName}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                            fontStyle: "italic",
                          }}
                        >
                          Pharmacist Added
                        </td>
                      </tr>
                    ))}

                    {/* Not Recommended Vaccines (selected) */}
                    {selectedNotRecVaccines.map((vaccine: any, idx: number) => {
                      const protocol = vaccineProtocolMap[vaccine.protocol_id];
                      return (
                        <tr key={`notrec-${idx}`}>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            V
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            {protocol?.displayName || vaccine.vaccine_name}
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              fontStyle: "italic",
                            }}
                          >
                            Pharmacist Selected
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Selected Medications Table */}
          {(() => {
            // Get all selected medications from AI recommendations
            const selectedAIMedications =
              aiRecommendations.recommendation.medications.filter(
                (m) => m.isSelected === true,
              );
            // Get manually added medications (from pharmacistAddedMedications)
            const selectedManualMedications = manualSelections.medications;
            // Get not recommended medications that are selected
            const selectedNotRecMedications = (
              aiRecommendations.recommendation.notRecommended?.medications || []
            ).filter((m: any) => m.isSelected === true);

            const hasMedications =
              selectedAIMedications.length > 0 ||
              selectedManualMedications.length > 0 ||
              selectedNotRecMedications.length > 0;

            if (!hasMedications) return null;

            return (
              <div>
                <h4
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#000",
                    margin: "0 0 6px 0",
                  }}
                >
                  Selected Medications
                </h4>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                          width: "30px",
                        }}
                      >
                        ✓
                      </th>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Medication Name
                      </th>
                      <th
                        style={{
                          borderBottom: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Source
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* AI Recommended Medications */}
                    {selectedAIMedications.map((medication, idx) => {
                      const protocol =
                        medicationProtocolMap[medication.protocol_id];
                      return (
                        <tr key={`ai-${idx}`}>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            M
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            {protocol?.displayName || medication.medication_name}
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #ddd",
                              padding: "4px",
                              fontStyle: "italic",
                            }}
                          >
                            AI Recommended
                          </td>
                        </tr>
                      );
                    })}

                    {/* Manually Added Medications */}
                    {selectedManualMedications.map((item, idx) => (
                      <tr key={`manual-${idx}`}>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          M
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                          }}
                        >
                          {item.protocol.displayName}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "4px",
                            fontStyle: "italic",
                          }}
                        >
                          Pharmacist Added
                        </td>
                      </tr>
                    ))}

                    {/* Not Recommended Medications (selected) */}
                    {selectedNotRecMedications.map(
                      (medication: any, idx: number) => {
                        const protocol =
                          medicationProtocolMap[medication.protocol_id];
                        return (
                          <tr key={`notrec-${idx}`}>
                            <td
                              style={{
                                borderBottom: "1px solid #ddd",
                                padding: "4px",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              M
                            </td>
                            <td
                              style={{
                                borderBottom: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {protocol?.displayName ||
                                medication.medication_name}
                            </td>
                            <td
                              style={{
                                borderBottom: "1px solid #ddd",
                                padding: "4px",
                                fontStyle: "italic",
                              }}
                            >
                              Pharmacist Selected
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Pharmacy Notes Section */}
      {notes.length > 0 && (
        <div
          style={{
            marginBottom: "6px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#000",
              margin: "0 0 12px 0",
              padding: "6px 0",
              borderBottom: "2px solid #000",
              letterSpacing: "0.5px",
            }}
          >
            Review Notes ({notes.length})
          </h3>
          <div>
            {notes
              .sort(
                (a, b) =>
                  new Date(b.timestamp).getTime() -
                  new Date(a.timestamp).getTime(),
              )
              .map((note, idx) => {
                const performedByName =
                  typeof note.performedBy === "object"
                    ? note.performedBy.name ||
                      note.performedBy.email ||
                      "Unknown"
                    : note.performedBy || "Unknown";

                const noteContent =
                  typeof note.details === "string"
                    ? note.details
                    : note.details?.note || "No content";

                return (
                  <div
                    key={idx}
                    style={{
                      padding: "8px 0",
                      marginBottom: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        lineHeight: "1.6",
                        marginBottom: "8px",
                        color: "#333333",
                      }}
                    >
                      {noteContent}
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#666666",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontWeight: "600", color: "#444444" }}>
                        {performedByName}
                      </span>
                      <span>{formatDateTime(note.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Trip Details Section */}
      {formData.tripDetails && (
        <div
          style={{
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#000",
              margin: "0 0 12px 0",
              padding: "6px 0",
              borderBottom: "2px solid #000",
              letterSpacing: "0.5px",
            }}
          >
            Trip Details
          </h3>
          <div
            style={{
              padding: "0",
            }}
          >
            <div style={{ fontSize: "11px" }}>
              <div
                style={{
                  display: "flex",
                  padding: "6px 0",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <span style={{ fontWeight: "bold", minWidth: "150px" }}>
                  Departure Date:
                </span>
                <span>{formatDate(formData.tripDetails.departureDate)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  padding: "6px 0",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <span style={{ fontWeight: "bold", minWidth: "150px" }}>
                  Return Date:
                </span>
                <span>{formatDate(formData.tripDetails.returnDate)}</span>
              </div>
            </div>

            {formData.tripDetails.destinations &&
              formData.tripDetails.destinations.length > 0 && (
                <>
                  <h4
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: "#000",
                      margin: "15px 0 10px 0",
                      textTransform: "uppercase",
                    }}
                  >
                    DESTINATIONS
                  </h4>
                  <div>
                    {formData.tripDetails.destinations.map(
                      (dest: any, index: number) => (
                        <div key={index}>
                          {index > 0 && (
                            <div
                              style={{
                                borderTop: "1px solid #ddd",
                                margin: "12px 0",
                              }}
                            />
                          )}
                          <div
                            style={{
                              display: "flex",
                              padding: "6px 0",
                            }}
                          >
                            <span
                              style={{ fontWeight: "bold", minWidth: "150px" }}
                            >
                              Destination #{dest.order || index + 1}:
                            </span>
                            <span>
                              {dest.destination_city
                                ? `${dest.destination_city}, `
                                : ""}
                              {dest.destination_country}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              padding: "6px 0",
                            }}
                          >
                            <span
                              style={{ fontWeight: "bold", minWidth: "150px" }}
                            >
                              Duration:
                            </span>
                            <span>
                              {dest.days_in_location} day
                              {dest.days_in_location !== 1 ? "s" : ""}
                            </span>
                          </div>
                          {dest.activities && dest.activities.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                padding: "6px 0",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "bold",
                                  minWidth: "150px",
                                }}
                              >
                                Activities:
                              </span>
                              <span>{dest.activities.join(", ")}</span>
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </>
              )}
          </div>
        </div>
      )}

      {/* Detailed Form Data - All Questions and Answers */}
      {formData.formData && typeof formData.formData === "object" && (
        <div
          style={{
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#000",
              margin: "0 0 12px 0",
              padding: "6px 0",
              borderBottom: "2px solid #000",
              letterSpacing: "0.5px",
            }}
          >
            Consultation Form - Questions and Answers
          </h3>
          {Object.entries(formData.formData)
            .sort(([sectionNameA], [sectionNameB]) => {
              // Define the desired order
              const sectionOrder: Record<string, number> = {
                personal_info: 0,
                medical_history: 1,
                medications: 2,
                vaccine_history: 3,
                consultation_requirements: 4,
                pharmacy_information: 5,
              };

              const orderA = sectionOrder[sectionNameA] ?? 999;
              const orderB = sectionOrder[sectionNameB] ?? 999;

              return orderA - orderB;
            })
            .map(([sectionName, sectionData]) => {
              if (!sectionData || typeof sectionData !== "object") return null;

              // Special handling for medical_history section
              if (sectionName === "medical_history") {
                const medicalHistory: any = sectionData;

                // Define detail fields that should be displayed prominently
                const detailFields = [
                  "allergies_details",
                  "heart_circulation_details",
                  "lungs_details",
                  "gastrointestinal_details",
                  "kidneys_details",
                  "immune_system_details",
                  "neurologic_psychiatric_details",
                  "musculoskeletal_skin_details",
                  "reproductive_health_details",
                  "other_conditions",
                  "surgeries",
                ];

                // Separate detail fields from yes/no fields
                const detailEntries: [string, any][] = [];
                const regularEntries: [string, any][] = [];

                Object.entries(medicalHistory).forEach(([key, value]) => {
                  if (detailFields.includes(key)) {
                    detailEntries.push([key, value]);
                  } else {
                    regularEntries.push([key, value]);
                  }
                });

                return (
                  <div
                    key={sectionName}
                    style={{
                      padding: "0",
                      marginBottom: "12px",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#000",
                        margin: "0 0 10px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      MEDICAL HISTORY
                    </h4>
                    <div style={{ fontSize: "11px" }}>
                      {/* Display regular yes/no fields */}
                      {regularEntries.map(([key, value]) => (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            padding: "6px 0",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          <span
                            style={{ fontWeight: "bold", minWidth: "200px" }}
                          >
                            {key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            :
                          </span>
                          <span>
                            {Array.isArray(value)
                              ? value.length > 0
                                ? value.join(", ")
                                : "None"
                              : typeof value === "object" && value !== null
                                ? JSON.stringify(value, null, 2)
                                : String(value) || "Not provided"}
                          </span>
                        </div>
                      ))}

                      {/* Display detail fields with more prominence */}
                      {detailEntries.filter(
                        ([_, value]) => value && String(value).trim(),
                      ).length > 0 && (
                        <>
                          <div
                            style={{
                              marginTop: "16px",
                              paddingTop: "16px",
                              borderTop: "2px solid #ddd",
                            }}
                          >
                            <h5
                              style={{
                                fontSize: "10px",
                                fontWeight: "bold",
                                color: "#000",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Medical History Details
                            </h5>
                          </div>
                          {detailEntries.map(([key, value]) => {
                            const stringValue = String(value || "").trim();
                            if (
                              !stringValue ||
                              stringValue === "undefined" ||
                              stringValue === "null"
                            )
                              return null;

                            return (
                              <div
                                key={key}
                                style={{
                                  marginBottom: "8px",
                                  paddingLeft: "8px",
                                  borderLeft: "3px solid #000",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    color: "#000",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {key
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  :
                                </div>
                                <div
                                  style={{
                                    fontSize: "10px",
                                    color: "#555555",
                                    lineHeight: "1.6",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {stringValue}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                );
              }

              // Special handling for vaccine_history section
              if (sectionName === "vaccine_history") {
                const vaccines: any = sectionData;
                // Access vaccines_received directly from vaccines (no nested data object)
                const vaccinesReceived = (
                  vaccines?.vaccines_received || []
                ).map((v: string) => v.trim());

                // Helper to get date directly from vaccines object
                const getVaccineDate = (dateKey: string) => {
                  return vaccines[dateKey] || null;
                };

                const vaccineList = [
                  {
                    name: "Tetanus (Td/Tdap)",
                    date:
                      getVaccineDate("tdap_date") ||
                      getVaccineDate("tetanus_date") ||
                      getVaccineDate("td_date"),
                    dataKey: "Tetanus (Td/Tdap)",
                  },
                  {
                    name: "MMR",
                    date: getVaccineDate("mmr_date"),
                    dataKey: "MMR",
                  },
                  {
                    name: "Polio",
                    date: getVaccineDate("polio_date"),
                    dataKey: "Polio",
                  },
                  {
                    name: "Hepatitis A",
                    date:
                      getVaccineDate("hepatitis_a_date") ||
                      getVaccineDate("hepa_date"),
                    dataKey: "Hepatitis A",
                  },
                  {
                    name: "Hepatitis B",
                    date:
                      getVaccineDate("hepatitis_b_date") ||
                      getVaccineDate("hepb_date"),
                    dataKey: "Hepatitis B",
                  },
                  {
                    name: "Influenza (Flu)",
                    date: getVaccineDate("influenza_date"),
                    dataKey: "Influenza (Flu)",
                  },
                  {
                    name: "COVID-19",
                    date:
                      getVaccineDate("covid_vaccine_date") ||
                      getVaccineDate("covid19_date"),
                    dataKey: "COVID-19",
                  },
                  {
                    name: "Typhoid",
                    date: getVaccineDate("typhoid_date"),
                    dataKey: "Typhoid",
                  },
                  {
                    name: "Yellow Fever",
                    date: getVaccineDate("yellow_fever_date"),
                    dataKey: "Yellow Fever",
                  },
                  {
                    name: "Rabies",
                    date: getVaccineDate("rabies_date"),
                    dataKey: "Rabies",
                  },
                  {
                    name: "Meningococcal",
                    date: getVaccineDate("meningococcal_date"),
                    dataKey: "Meningococcal",
                  },
                  {
                    name: "Japanese Encephalitis",
                    date:
                      getVaccineDate("je_date") ||
                      getVaccineDate("japanese_encephalitis_date"),
                    dataKey: "Japanese Encephalitis",
                  },
                  {
                    name: "HPV",
                    date: getVaccineDate("hpv_date"),
                    dataKey: "HPV",
                  },
                  {
                    name: "Shingles (Zoster)",
                    date:
                      getVaccineDate("shingles_date") ||
                      getVaccineDate("zoster_date"),
                    dataKey: "Shingles (Zoster)",
                  },
                  {
                    name: "Chickenpox (Varicella)",
                    date:
                      getVaccineDate("chickenpox_date") ||
                      getVaccineDate("varicella_date"),
                    dataKey: "Chickenpox (Varicella)",
                  },
                  {
                    name: "RSV",
                    date: getVaccineDate("rsv_date"),
                    dataKey: "RSV",
                  },
                  {
                    name: "Cholera",
                    date: getVaccineDate("cholera_date"),
                    dataKey: "Cholera",
                  },
                  {
                    name: "Tick-Borne Encephalitis",
                    date:
                      getVaccineDate("tick_borne_encephalitis_date") ||
                      getVaccineDate("tbe_date"),
                    dataKey: "Tick-Borne Encephalitis",
                  },
                  {
                    name: "Pneumococcal (Pneumonia)",
                    date:
                      getVaccineDate("pneumococcal_date") ||
                      getVaccineDate("pneumonia_date"),
                    dataKey: "Pneumococcal (Pneumonia)",
                  },
                ];

                // Check if we have any vaccines to display (case-insensitive comparison)
                const hasVaccines = vaccineList.some((vaccine) => {
                  const isInReceivedArray = vaccinesReceived.some(
                    (v: string) =>
                      v.toLowerCase() === vaccine.dataKey.toLowerCase(),
                  );
                  return isInReceivedArray || !!vaccine.date;
                });

                return (
                  <div
                    key={sectionName}
                    style={{
                      padding: "0",
                      marginBottom: "12px",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#000",
                        margin: "0 0 10px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      VACCINATION STATUS
                    </h4>
                    <div style={{ fontSize: "11px" }}>
                      {hasVaccines ? (
                        <>
                          {vaccineList.map((vaccine, idx) => {
                            // Case-insensitive comparison for vaccine names
                            const isInReceivedArray = vaccinesReceived.some(
                              (v: string) =>
                                v.toLowerCase() ===
                                vaccine.dataKey.toLowerCase(),
                            );
                            const hasDate = !!vaccine.date;
                            const isVaccinated = isInReceivedArray || hasDate;

                            // Only show vaccines that were received
                            if (!isVaccinated) return null;

                            return (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  padding: "6px 0",
                                  borderBottom: "1px solid #ddd",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    minWidth: "200px",
                                  }}
                                >
                                  {vaccine.name}:
                                </span>
                                <span>
                                  Received
                                  {vaccine.date && (
                                    <>
                                      {" - Date: "}
                                      <span
                                        style={{
                                          color: "#000",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {formatDate(vaccine.date)}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            padding: "6px 0",
                          }}
                        >
                          <span style={{ color: "#666", fontStyle: "italic" }}>
                            No vaccines received
                          </span>
                        </div>
                      )}
                      {vaccines.vaccine_records_available !== undefined && (
                        <div
                          style={{
                            display: "flex",
                            marginTop: "12px",
                            paddingTop: "12px",
                            borderTop: "1px solid #ddd",
                          }}
                        >
                          <span
                            style={{ fontWeight: "bold", minWidth: "200px" }}
                          >
                            Vaccine Records Available:
                          </span>
                          <span>
                            {vaccines.vaccine_records_available === "true" ||
                            vaccines.vaccine_records_available === true
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={sectionName}
                  style={{
                    padding: "0",
                    marginBottom: "12px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: "#000",
                      margin: "0 0 10px 0",
                      textTransform: "uppercase",
                    }}
                  >
                    {sectionName.replace(/_/g, " ").toUpperCase()}
                  </h4>
                  <div style={{ fontSize: "11px" }}>
                    {Object.entries(sectionData as Record<string, any>).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            padding: "6px 0",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          <span
                            style={{ fontWeight: "bold", minWidth: "200px" }}
                          >
                            {key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            :
                          </span>
                          <span>
                            {Array.isArray(value)
                              ? value.length > 0
                                ? value.join(", ")
                                : "None"
                              : typeof value === "object" && value !== null
                                ? JSON.stringify(value, null, 2)
                                : String(value) || "Not provided"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* End of Document - Signature Section */}
      <div
        style={{
          marginTop: "20px",
          paddingTop: "15px",
          borderTop: "2px solid #000",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "6px",
            fontSize: "10px",
          }}
        >
          <div>
            <strong>Pharmacist Name:</strong> _________________________
          </div>
          <div>
            <strong>NPI #:</strong> _________________________
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "10px",
          }}
        >
          <div>
            <strong>Signature:</strong> _________________________
          </div>
          <div>
            <strong>Date:</strong> _________________________
          </div>
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: "12px",
            fontSize: "10px",
            fontStyle: "italic",
          }}
        >
          <strong>{currentPharmacy?.name || "Voyax Health"}</strong>
          {currentPharmacy?.location && ` | ${currentPharmacy.location}`}
        </div>
      </div>
    </div>
  );
};
